import { Method } from 'axios';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@tools/ui/api';

export function useApiRequest<ResponseType>(options: {
  method: Method;
  path: string;
  queryParams?: object;
  body?: object;
  skip?: boolean;
}) {
  const [refetchCounter, setRefetchCounter] = useState(0);
  const params = useRef<object | undefined>(undefined);
  const body = useRef<object | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'error' | ResponseType>(
    'loading'
  );

  params.current = isEqual(params.current, options.queryParams)
    ? params.current
    : options.queryParams;

  body.current = isEqual(body.current, options.body)
    ? body.current
    : options.body;

  const refetch = useCallback(() => {
    setRefetchCounter((currentValue) => currentValue + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (!options.skip) {
      setState('loading');

      api
        .request({
          method: options.method,
          url: options.path,
          params: params.current,
          data: body.current,
          signal: controller.signal
        })
        .then((response) => setState(response as ResponseType))
        .catch((error) => {
          setState(error?.message === 'canceled' ? 'loading' : 'error');
        });
    }

    return () => {
      controller.abort();
    };
  }, [
    options.method,
    options.path,
    params.current,
    body.current,
    options.skip,
    refetchCounter
  ]);

  return {
    loading: state === 'loading',
    error: state === 'error',
    data: typeof state === 'string' ? undefined : state,
    refetch
  };
}
